// The conversation with the seller on a quote (My Quotes messages): what we sent,
// what they answered. One hook and two pure readers, shared by every screen that
// shows it (Quote Assessment, the deal modal, Issue Trades).
//
// The worker route is GET /api/deals/:id/customer-messages. It returns the
// messages oldest first and `reply_enabled`, the state of the reply switch.

import { useCallback, useEffect, useState } from 'react';
import { api } from '../../../utils/api';

export const MAX_REPLY_LENGTH = 2000;

export const KIND_LABELS = {
    more_info: 'More info request',
    fair_price: 'Fair price request',
    quote_ready: 'Quote ready',
    consignment_offer: 'Consignment offer',
    rejected: 'Quote closed',
    item_issue: 'Item issue',
    reply: 'Message',
    item_decision: 'Item decision',
};

/** `?quote_id=` when the caller holds the Prism quote id, so the worker skips a lookup. */
function hint(quoteId) {
    return quoteId ? `?quote_id=${encodeURIComponent(quoteId)}` : '';
}

/**
 * Load the conversation for one quote.
 * `status` is 'loading', 'ready' or 'error'. A failed load never throws into the screen.
 */
export function useCustomerMessages(dealId, quoteId) {
    const [state, setState] = useState({ status: 'loading', messages: [], replyEnabled: false });
    const [version, setVersion] = useState(0);

    useEffect(() => {
        if (!dealId) { setState({ status: 'ready', messages: [], replyEnabled: false }); return undefined; }
        let alive = true;
        api.get(`/api/deals/${encodeURIComponent(dealId)}/customer-messages${hint(quoteId)}`)
            .then((r) => {
                if (alive) setState({ status: 'ready', messages: r?.messages ?? [], replyEnabled: r?.reply_enabled === true });
            })
            .catch(() => { if (alive) setState({ status: 'error', messages: [], replyEnabled: false }); });
        return () => { alive = false; };
    }, [dealId, quoteId, version]);

    const reload = useCallback(() => setVersion((v) => v + 1), []);
    const reply = useCallback(
        (body, inReplyTo) => api.post(
            `/api/deals/${encodeURIComponent(dealId)}/customer-messages/reply${hint(quoteId)}`,
            { body, in_reply_to: inReplyTo || undefined },
        ),
        [dealId, quoteId],
    );
    return { ...state, reload, reply };
}

/**
 * The seller's latest decision per item: Map of quote_item_id to
 * { decision: 'accept' | 'return', price_cents }. A later message replaces an earlier one.
 */
export function itemDecisions(messages) {
    const decisions = new Map();
    for (const m of messages ?? []) {
        if (m.kind !== 'item_decision' || m.direction !== 'from_customer') continue;
        const id = m.details?.quote_item_id;
        if (id && (m.details.decision === 'accept' || m.details.decision === 'return')) {
            decisions.set(id, { decision: m.details.decision, price_cents: m.details.price_cents ?? null });
        }
    }
    return decisions;
}

/** The newest message from the customer, or null. The reply answers this one. */
export function latestCustomerMessage(messages) {
    return [...(messages ?? [])].reverse().find((m) => m.direction === 'from_customer') ?? null;
}

/** The badge text for one item's decision, or null when the seller has not decided. */
export function decisionBadgeText(decision, formatMoney) {
    if (!decision) return null;
    return decision.decision === 'accept'
        ? `Seller accepted ${formatMoney(decision.price_cents)}`
        : 'Seller wants it back';
}
